"use client"

import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Receipt, AlertCircle, ExternalLink, CheckCircle2, Copy, Barcode } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_API_URL || "https://api.ticketdovaqueiro.com/api";

export default function CheckoutBoletoAsaasCircuito({ pedidoId, valor }) {
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    cpfCnpj: "",
    phone: "",
  });
  
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState("");
  const [boletoUrl, setBoletoUrl] = useState("");
  const [linhaDigitavel, setLinhaDigitavel] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [copiado, setCopiado] = useState(false);

  useEffect(() => {
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      if (user) {
        const nomes = (user.nomeCompleto || "").trim().split(" ");
        setForm({
          firstName: nomes[0] || "",
          lastName: nomes.slice(1).join(" ") || "",
          email: user.email || "",
          cpfCnpj: user.cpf || "",
          phone: user.whatsapp || "",
        });
      }
    } catch (e) {
      console.error("Erro ao carregar dados do usuário:", e);
    }
  }, []);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const copiarCodigo = () => {
    if (!linhaDigitavel) return;
    navigator.clipboard.writeText(linhaDigitavel);
    setCopiado(true);
    setTimeout(() => setCopiado(false), 2000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErro("");
    
    try {
      const token = localStorage.getItem("token");
      const { data } = await axios.post(
        `${API}/pagamento/asaas/boleto`,
        {
          pedidoId,
          totalPedido: Number(valor),
          nomeVaqueiro: `${form.firstName} ${form.lastName}`.trim(),
          email: form.email,
          cpfCnpj: form.cpfCnpj,
          phone: form.phone,
          descricao: `Inscrição Circuito - Pedido ${pedidoId}`,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setBoletoUrl(data?.boleto_url || "");
      setLinhaDigitavel(data?.linha_digitavel || "");
      setDueDate(data?.dueDate || "");
    } catch (err) {
      console.error("Erro Asaas boleto Circuito:", err?.response?.data || err.message);
      
      let msg = err?.response?.data?.detail || err?.response?.data?.error || "Erro ao gerar boleto Asaas.";
      
      if (typeof msg === "object") {
        msg = msg?.errors?.[0]?.description || JSON.stringify(msg);
      }
      
      setErro(String(msg));
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4 }}
      style={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%" }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 24 }}>
        {/* 💡 Ícone com a cor do circuito, texto forçado em branco para não herdar o amarelo global */}
        <Barcode size={24} color="var(--c-accent)" />
        <h3 style={{ fontSize: "18px", fontWeight: 700, color: "#fff", margin: 0 }}>Pagamento via Boleto</h3>
      </div>

      {erro && (
        <motion.div 
          initial={{ opacity: 0, y: -10 }} 
          animate={{ opacity: 1, y: 0 }} 
          style={{ 
            display: "flex", alignItems: "center", gap: 10, width: "100%", maxWidth: 360,
            padding: "16px", background: "rgba(239,68,68,0.1)", borderRadius: 12, 
            border: "1px solid rgba(239,68,68,0.2)", color: "#f87171", marginBottom: 24, fontSize: "14px"
          }}
        >
          <AlertCircle size={20} style={{ flexShrink: 0 }} />
          <span>{erro}</span>
        </motion.div>
      )}

      {boletoUrl ? (
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }} 
          style={{ width: "100%", maxWidth: 360, display: "flex", flexDirection: "column", alignItems: "center" }}
        >
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginBottom: 24 }}>
            <CheckCircle2 size={56} color="var(--c-accent)" style={{ marginBottom: 16 }} />
            <h3 style={{ fontSize: "20px", fontWeight: 800, color: "#fff", margin: 0, textAlign: "center" }}>
              Boleto Gerado!
            </h3>
            <p style={{ opacity: 0.7, color: "#fff", fontSize: "14px", textAlign: "center", marginTop: 8 }}>
              Sua inscrição foi registrada. O pagamento via boleto pode levar até <strong>3 dias úteis</strong> para ser compensado.
            </p>
          </div>

          <div style={{ width: "100%", background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 16, padding: 20, marginBottom: 24 }}>
            {dueDate && (
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16, borderBottom: "1px dashed rgba(255,255,255,0.1)", paddingBottom: 12 }}>
                <span style={{ opacity: 0.6, color: "#fff", fontSize: "14px" }}>Vencimento</span>
                <strong style={{ color: "#fff", fontSize: "15px" }}>
                  {(() => {
                    const [y, m, d] = (dueDate || "").split("-");
                    return d && m && y ? `${d}/${m}/${y}` : dueDate;
                  })()}
                </strong>
              </div>
            )}
            
            <div style={{ width: "100%" }}>
              <label style={{ display: "block", fontSize: "13px", color: "#fff", opacity: 0.7, marginBottom: 8, fontWeight: 500 }}>
                Linha Digitável:
              </label>
              <div style={{ display: "flex", gap: 8 }}>
                <input 
                  type="text" 
                  value={linhaDigitavel} 
                  readOnly 
                  className="c-input" 
                  style={{ flex: 1, height: "48px", fontSize: "12px", color: "#fff", background: "rgba(0,0,0,0.2)" }} 
                  onClick={(e) => e.target.select()}
                />
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={copiarCodigo}
                  className="c-btn c-btn--primary"
                  style={{ width: "48px", height: "48px", padding: 0, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
                >
                  {copiado ? <CheckCircle2 size={20} /> : <Copy size={20} />}
                </motion.button>
              </div>
              {copiado && (
                <motion.div initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} style={{ color: "var(--c-accent)", fontSize: "12px", marginTop: 8, textAlign: "right", fontWeight: 500 }}>
                  Código copiado com sucesso!
                </motion.div>
              )}
            </div>
          </div>

          <a
            href={boletoUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: "none", width: "100%" }}
          >
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="c-btn c-btn--primary"
              style={{ width: "100%", height: "52px", fontSize: "15px", fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}
            >
              <ExternalLink size={18} />
              Visualizar e Imprimir Boleto
            </motion.button>
          </a>
        </motion.div>
      ) : (
        <form onSubmit={handleSubmit} style={{ width: "100%", maxWidth: 360, display: "flex", flexDirection: "column", gap: 16 }}>
          
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label style={{ display: "block", marginBottom: 6, fontSize: "13px", color: "#fff", opacity: 0.8, fontWeight: 500 }}>Nome</label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={form.firstName}
                onChange={onChange}
                placeholder="Nome"
                className="c-input"
                style={{ width: "100%", height: "48px", paddingLeft: "16px", color: "#fff" }}
                required
              />
            </div>
            <div>
              <label style={{ display: "block", marginBottom: 6, fontSize: "13px", color: "#fff", opacity: 0.8, fontWeight: 500 }}>Sobrenome</label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={form.lastName}
                onChange={onChange}
                placeholder="Sobrenome"
                className="c-input"
                style={{ width: "100%", height: "48px", paddingLeft: "16px", color: "#fff" }}
                required
              />
            </div>
          </div>

          <div>
            <label style={{ display: "block", marginBottom: 6, fontSize: "13px", color: "#fff", opacity: 0.8, fontWeight: 500 }}>E-mail</label>
            <input
              type="email"
              id="email"
              name="email"
              value={form.email}
              onChange={onChange}
              placeholder="seu@email.com"
              className="c-input"
              style={{ width: "100%", height: "48px", paddingLeft: "16px", color: "#fff" }}
              required
            />
          </div>

          <div>
            <label style={{ display: "block", marginBottom: 6, fontSize: "13px", color: "#fff", opacity: 0.8, fontWeight: 500 }}>CPF ou CNPJ</label>
            <input
              type="text"
              id="cpfCnpj"
              name="cpfCnpj"
              value={form.cpfCnpj}
              onChange={onChange}
              placeholder="Somente números"
              className="c-input"
              style={{ width: "100%", height: "48px", paddingLeft: "16px", color: "#fff" }}
              required
            />
          </div>

          <motion.button
            type="submit"
            disabled={loading}
            className="c-btn c-btn--primary"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            style={{ width: "100%", height: "52px", marginTop: "16px", fontSize: "16px", fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}
          >
            {loading ? (
              <span style={{ opacity: 0.8 }}>Gerando Boleto...</span>
            ) : (
              <>
                <Receipt size={20} />
                Gerar Boleto
              </>
            )}
          </motion.button>
        </form>
      )}
    </motion.div>
  );
}